/*
 * Recently viewed products.
 *
 * The visitor's history lives in localStorage so the product page itself stays
 * fully cacheable. The row is filled in from a single request to the
 * search.recently-viewed alternate template, fired only once the section gets
 * close to the viewport.
 */

const RECENTLY_VIEWED_STORAGE_KEY = 'poolgoods:recently-viewed';

// Deliberately larger than any sensible "products to show" setting, so raising
// that setting still has history to draw on.
const RECENTLY_VIEWED_MAX_STORED = 20;

// Ask for a few more products than we intend to display. Anything since
// unpublished, archived or sold out of every variant simply won't come back from
// search, and this keeps the carousel full instead of quietly shrinking.
const RECENTLY_VIEWED_OVERFETCH = 4;

function readRecentlyViewed() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(RECENTLY_VIEWED_STORAGE_KEY));
    if (!Array.isArray(stored)) return [];
    return stored.filter((id) => Number.isInteger(id));
  } catch (error) {
    // Storage blocked (private browsing) or a corrupted payload. Treat as "no history".
    return [];
  }
}

function recordRecentlyViewed(productId) {
  if (!Number.isInteger(productId)) return;

  const ids = [productId, ...readRecentlyViewed().filter((id) => id !== productId)];

  try {
    window.localStorage.setItem(
      RECENTLY_VIEWED_STORAGE_KEY,
      JSON.stringify(ids.slice(0, RECENTLY_VIEWED_MAX_STORED))
    );
  } catch (error) {
    // Storage blocked or full. This visit just won't be remembered.
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define(
    'recently-viewed-products',
    class RecentlyViewedProducts extends HTMLElement {
      observer = undefined;

      connectedCallback() {
        const currentProductId = Number.parseInt(this.dataset.productId, 10);
        this.limit = Number.parseInt(this.dataset.productsToShow, 10) || 8;

        // Read the history *before* recording this visit, so the product being
        // viewed never shows up in its own recently viewed row.
        this.productIds = readRecentlyViewed()
          .filter((id) => id !== currentProductId)
          .slice(0, this.limit + RECENTLY_VIEWED_OVERFETCH);

        recordRecentlyViewed(currentProductId);

        // Nothing to show yet — leave the section hidden and skip the request.
        if (this.productIds.length === 0) return;

        this.observer = new IntersectionObserver(
          (entries, observer) => {
            if (!entries[0].isIntersecting) return;
            observer.unobserve(this);
            this.loadProducts();
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.unobserve(this);
        this.slider?.destroy(true, true);
      }

      loadProducts() {
        const query = this.productIds.map((id) => `id:${id}`).join(' OR ');

        fetch(`${this.dataset.url}&q=${encodeURIComponent(query)}`)
          .then((response) => {
            if (!response.ok) throw new Error(`Request failed with ${response.status}`);
            return response.text();
          })
          .then((markup) => {
            const wrapper = this.querySelector('.swiper-wrapper');
            if (!wrapper) return;

            wrapper.innerHTML = markup;
            this.sortByRecency(wrapper);

            if (wrapper.children.length === 0) return;

            this.classList.add('recently-viewed-products--loaded');
            this.initSlider();
          })
          .catch((error) => {
            // Leave the section hidden rather than showing an empty row.
            console.error('Recently viewed products failed to load:', error);
          });
      }

      /*
       * Search returns matches by relevance, so re-order the cards to
       * most-recently-viewed first and drop anything that came back without a
       * matching id (a product that has since been unpublished, say). The
       * over-fetched extras are trimmed off here, once we know how many of the
       * requested products actually came back.
       */
      sortByRecency(wrapper) {
        const cards = new Map();

        wrapper.querySelectorAll(':scope > [data-product-id]').forEach((card) => {
          cards.set(Number.parseInt(card.dataset.productId, 10), card);
        });

        const ordered = this.productIds
          .map((id) => cards.get(id))
          .filter(Boolean)
          .slice(0, this.limit);

        wrapper.replaceChildren(...ordered);
      }

      /*
       * Swiper is loaded globally by theme.liquid. Elements are passed by
       * reference rather than selector so this instance can only ever control
       * its own arrows and pagination.
       */
      initSlider() {
        const container = this.querySelector('.swiper-container');
        if (!container || typeof window.Swiper === 'undefined') return;

        const perView = Number.parseInt(container.dataset.sliderItems, 10) || 4;

        this.slider = new window.Swiper(container, {
          /*
           * This theme ships Swiper 4, where `breakpoints` keys are MAX widths —
           * the smallest key >= the viewport wins, and anything wider than the
           * largest key falls back to the top level value. Swiper 5 inverted
           * this to min-width, so config copied from newer docs will silently
           * apply the top level value everywhere. Widest case goes on top.
           */
          slidesPerView: perView,
          spaceBetween: 0,
          watchOverflow: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            type: 'fraction',
          },
          // Same steps as the featured products carousel in custom.js, so both
          // rows show the same number of cards at any given width.
          breakpoints: {
            1350: { slidesPerView: Math.max(perView - 1, 1) },
            990: { slidesPerView: 3 },
            640: { slidesPerView: 2.1 },
          },
        });

        this.toggleNav();
        this.slider.on('resize', () => {
          // Params are applied after the resize handlers run, so read them next frame.
          window.requestAnimationFrame(() => this.toggleNav());
        });
      }

      /*
       * With fewer products than fit on screen there is nothing to page through,
       * so drop the arrows and the "1 / 1" counter rather than showing dead controls.
       */
      toggleNav() {
        const nav = this.querySelector('.recently-viewed__nav');
        if (!nav || !this.slider) return;

        nav.hidden = this.slider.isBeginning && this.slider.isEnd;
      }
    }
  );
}
